import {
  FormControlLabel,
  Checkbox,
  TextField,
  Box,
  Card,
  CardMedia,
  Typography,
  IconButton,
  Button,
} from '@mui/material';
import React, { useState } from 'react';
import { blobUrl, uploadType } from '../Constants';
import WarningIcon from '@mui/icons-material/Warning';
import SaveIcon from '@mui/icons-material/Save';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import { Upload, Video } from '../@types/api';
import ModalFileManager from './Common/ModalFileManager';

interface Props {
  video: Video;
  onSaveVideo: (video: Video) => void;
  onRemoveVideo: (video: Video) => void;
  isSelected: boolean;
}
const VideoCard: React.FC<Props> = ({
  video,
  onSaveVideo,
  isSelected,
  onRemoveVideo,
}) => {
  const [orderIndex, setOrderIndex] = useState<number>(video.orderIndex);
  const [isLive, setIsLive] = useState<boolean>(video.isLive);
  const [imageUpload, setImageUpload] = useState<Upload | undefined>(
    video.imageUpload
  );
  const [shouldDelete, setShouldDelete] = useState<boolean>(false);
  const [show, setShow] = useState(false);
  const isDirty =
    shouldDelete ||
    orderIndex !== video.orderIndex ||
    isLive !== video.isLive ||
    imageUpload?.uploadId !== video.imageUpload?.uploadId;
  const videoUrl = `${blobUrl}/videos/${video.videoUpload.fileName}`;
  const imageUrl = imageUpload
    ? `${blobUrl}/images/${imageUpload.fileName}`
    : undefined;

  const onNewPosterSelected = (upload: Upload) => {
    setImageUpload(upload);
    setShow(false);
  };

  const handleClose = () => {
    setShow(false);
  };

  const handleShow = () => {
    setShow(true);
  };

  const handleSave = () => {
    const updatedVideo = {
      ...video,
      isLive,
      orderIndex,
      imageUploadId: imageUpload?.uploadId ?? video.imageUploadId,
      imageUpload: imageUpload ?? video.imageUpload,
    };
    onSaveVideo(updatedVideo);
  };

  const handleRemoveVideo = () => {
    onRemoveVideo(video);
  };

  const handleReset = () => {
    setIsLive(video.isLive);
    setOrderIndex(video.orderIndex);
    setImageUpload(video.imageUpload);
    setShouldDelete(false);
  };

  return (
    <>
      <Card sx={{ maxWidth: 345 }} raised={isSelected}>
        <Box
          pt={1}
          style={{
            backgroundColor: 'black',
            height: '170px',
            display: 'flex',
            alignItems: 'flex-end',
            position: 'relative',
          }}
        >
          <Typography
            position={'absolute'}
            color={'whitesmoke'}
            sx={{ top: 7, left: 10 }}
          >
            {video.videoUpload.altText}
          </Typography>
          <video width='100%' controls>
            <source src={videoUrl} type='video/mp4' />
          </video>
        </Box>
        {imageUrl && (
          <CardMedia sx={{ height: 140 }} image={imageUrl} title='poster' />
        )}

        <Box
          justifyContent='space-between'
          display='flex'
          alignItems='center'
          sx={{ width: '100%', p: 1 }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={isLive}
                onChange={() => setIsLive(!isLive)}
                disabled={!isSelected}
              />
            }
            label='Live'
          />
          <IconButton
            aria-label='change photo'
            size='small'
            title='change photo'
            onClick={handleShow}
            disabled={!isSelected}
          >
            <PhotoCameraIcon color={isSelected ? 'info' : 'disabled'} />
          </IconButton>
          <TextField
            label='Order'
            disabled={!isSelected}
            type='number'
            variant='outlined'
            value={orderIndex}
            onChange={(e) => setOrderIndex(Number(e.currentTarget.value))}
            InputProps={{ inputProps: { min: 0 } }}
            size='small'
            sx={{ maxWidth: 60 }}
          />
        </Box>

        <Box
          justifyContent='space-between'
          display='flex'
          alignItems='center'
          sx={{ width: '100%' }}
        >
          <Button
            aria-label='cancel'
            onClick={handleReset}
            disabled={!isDirty}
            title='cancel'
            startIcon={
              <CancelIcon color={isDirty ? 'secondary' : 'disabled'} />
            }
          >
            Undo
          </Button>
          <Button
            aria-label='delete'
            onClick={handleRemoveVideo}
            title='delete'
            startIcon={<DeleteIcon color={'warning'} />}
          >
            Delete
          </Button>
          {isDirty && <WarningIcon color='warning' />}
          <Button
            aria-label='save'
            onClick={handleSave}
            title='save changes'
            startIcon={<SaveIcon color={isDirty ? 'action' : 'disabled'} />}
          >
            Save
          </Button>
        </Box>
      </Card>
      <ModalFileManager
        show={show}
        handleClose={handleClose}
        fileType={uploadType.poster}
        handleUploadSelect={onNewPosterSelected}
      />
    </>
  );
};

export default VideoCard;
